import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext.js";
import { cognitoErrorCode, cognitoErrorMessage } from "../lib/auth.js";
import { useSignupMode } from "../hooks/useSignupMode.js";
import { isTurnstileConfigured } from "../lib/captcha.js";
import { registerTitle, registerView } from "../lib/registerMode.js";
import { formatPageTitle } from "../lib/pageTitle.js";
import { TurnstileWidget } from "../components/TurnstileWidget.js";
import { WaitlistRequestForm } from "../components/WaitlistRequestForm.js";
import {
  AuthForm,
  AuthShell,
  ErrorBanner,
  fieldCls,
  labelCls,
  linkCls,
} from "../components/AuthShell.js";
import { Button } from "../components/ui/Button.js";

const PASSWORD_HINT =
  "At least 8 characters, with upper, lower, a number, and a symbol.";

function invitedHref(email: string): string {
  const params = new URLSearchParams({ invited: "1" });
  if (email) params.set("email", email);
  return `/register?${params.toString()}`;
}

function AlreadyApprovedLink({ email }: { email?: string }) {
  return (
    <p className="pt-1 text-center text-sm text-muted">
      <Link to={invitedHref(email ?? "")} className={linkCls}>
        Already approved? Create your account
      </Link>
    </p>
  );
}

export function RegisterPage() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invited = searchParams.get("invited") === "1";
  const prefillEmail = searchParams.get("email") ?? "";

  const { mode: loadedMode, loaded } = useSignupMode();
  const mode = loaded ? loadedMode : "loading";
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const captchaRequired = isTurnstileConfigured();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [widgetNonce, setWidgetNonce] = useState(0);
  const handleVerify = useCallback((token: string | null) => {
    setCaptchaToken(token);
  }, []);

  useEffect(() => {
    if (prefillEmail) setEmail(prefillEmail);
  }, [prefillEmail]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (captchaRequired && !captchaToken) {
      setError("Please complete the bot check below.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await signUp(
        email.trim(),
        password,
        captchaToken ?? undefined,
      );
      if (result.userConfirmed) {
        navigate(`/login?email=${encodeURIComponent(email.trim())}`);
      } else {
        navigate(`/verify?email=${encodeURIComponent(email.trim())}`);
      }
    } catch (err) {
      setCaptchaToken(null);
      setWidgetNonce((n) => n + 1);
      const code = cognitoErrorCode(err);
      if (code === "UsernameExistsException") {
        setError("An account with that email already exists. Try signing in.");
      } else if (code === "InvalidPasswordException") {
        setError(cognitoErrorMessage(err, PASSWORD_HINT));
      } else {
        setError(cognitoErrorMessage(err, "Could not create account."));
      }
    } finally {
      setBusy(false);
    }
  }

  const view = mode === "loading" ? null : registerView(mode, invited);

  useEffect(() => {
    document.title = formatPageTitle(registerTitle(view));
  }, [view]);

  if (view === null) {
    return (
      <AuthShell title="Create account">
        <p className="text-sm text-muted">Checking sign-up…</p>
      </AuthShell>
    );
  }

  if (view === "request") {
    return (
      <AuthShell
        title="Request access"
        subtitle="Sign-up is invite-only. Leave your email and we'll write if a spot opens."
      >
        <WaitlistRequestForm initialEmail={prefillEmail} />
        <AlreadyApprovedLink email={prefillEmail} />
      </AuthShell>
    );
  }

  if (view === "closed") {
    return (
      <AuthShell title="Sign-up is closed" subtitle="New accounts aren't being accepted right now.">
        <AlreadyApprovedLink email={prefillEmail} />
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Create account" subtitle="Free signup — verify your email to continue.">
      <AuthForm onSubmit={(e) => void handleSubmit(e)}>
        <ErrorBanner message={error} />

        <div>
          <label htmlFor="email" className={labelCls}>
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            className={fieldCls}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="password" className={labelCls}>
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            className={fieldCls}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="mt-1 text-xs text-muted-sm">{PASSWORD_HINT}</p>
        </div>

        <div>
          <label htmlFor="confirm" className={labelCls}>
            Confirm password
          </label>
          <input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            className={fieldCls}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        {captchaRequired && (
          <TurnstileWidget
            key={widgetNonce}
            onVerify={handleVerify}
            action="signup"
          />
        )}

        <Button
          type="submit"
          fullWidth
          disabled={busy || (captchaRequired && !captchaToken)}
        >
          {busy ? "Creating…" : "Create account"}
        </Button>

        <p className="pt-1 text-center text-sm text-muted">
          Already have an account?{" "}
          <Link to="/login" className={linkCls}>
            Sign in
          </Link>
        </p>
      </AuthForm>
    </AuthShell>
  );
}
